import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { Session, User } from '@supabase/supabase-js';
import { officeAuthService, AuthUser } from './office-auth.service';
import { officeReadyPromise } from '@/services/office-ready';

interface AuthContextValue {
  user: AuthUser | null;
  session: Session | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signInWithToken: (accessToken: string, refreshToken: string) => Promise<void>;
  signInSilentSSO: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function userFromSupabase(user: User): AuthUser {
  return {
    id: user.id,
    email: user.email ?? '',
    name: user.user_metadata?.full_name ?? user.email ?? '',
    avatar: user.user_metadata?.avatar_url,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check existing session on mount, then auto-attempt silent SSO
  useEffect(() => {
    let cancelled = false;
    let subscription: { unsubscribe: () => void } | null = null;

    const init = async () => {
      try {
        // Check for an existing Supabase session first (instant, no Office.js needed)
        const { session, user } = await officeAuthService.getCurrentSession();
        if (!cancelled && session && user) {
          setSession(session);
          setUser(userFromSupabase(user));
          setLoading(false);
          return;
        }

        // Wait for Office.js to be fully initialized before attempting SSO
        console.log('[AuthProvider] Waiting for Office.js to initialize...');
        await officeReadyPromise;
        if (cancelled) return;

        console.log('[AuthProvider] Office.js ready, attempting silent SSO...');
        try {
          const ssoResult = await officeAuthService.signInSilent();
          if (!cancelled) {
            console.log('[AuthProvider] Silent SSO succeeded');
            setSession(ssoResult.session);
            setUser(ssoResult.user);
          }
        } catch (err) {
          if (!cancelled) {
            console.log('[AuthProvider] Silent SSO not available, showing login form:', err);
          }
        }
      } catch (err) {
        console.error('[AuthProvider] Auth init failed:', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }

      // Subscribe to auth state changes (token refresh, sign out, etc.)
      if (!cancelled) {
        try {
          const supabase = officeAuthService.getSupabase();
          const { data } = supabase.auth.onAuthStateChange(
            (_event, session) => {
              if (cancelled) return;
              if (session) {
                setSession(session);
                setUser(userFromSupabase(session.user));
              } else {
                setSession(null);
                setUser(null);
              }
            },
          );
          subscription = data.subscription;
        } catch (err) {
          console.error('[AuthProvider] Failed to set up auth listener:', err);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await officeAuthService.signInWithPassword(email, password);
      setSession(result.session);
      setUser(result.user);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signInWithToken = useCallback(async (accessToken: string, refreshToken: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await officeAuthService.signInWithToken(accessToken, refreshToken);
      setSession(result.session);
      setUser(result.user);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Token sign in failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signInSilentSSO = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await officeAuthService.signInSilent();
      setSession(result.session);
      setUser(result.user);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Silent SSO failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await officeAuthService.signOut();
      setSession(null);
      setUser(null);
    } catch (err) {
      console.error('Sign out failed:', err);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    session,
    isAuthenticated: !!session && !!user,
    loading,
    error,
    signInWithPassword,
    signInWithToken,
    signInSilentSSO,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
